import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAnalyzeClip } from '@/api/hooks/useOpenAI'
import { useOpenAIStatus } from '@/api/hooks/useOpenAI'
import type { AiLog } from '@/types'
import { Bot, Loader2, ChevronDown } from 'lucide-react'

interface AiLogDetailProps {
  aiLog: AiLog
  clipFilename?: string
}

export function AiLogDetail({ aiLog, clipFilename }: AiLogDetailProps) {
  const analyzeClip = useAnalyzeClip()
  const { data: openaiStatus } = useOpenAIStatus()
  const [gptResult, setGptResult] = useState<string | null>(null)
  const [showGpt, setShowGpt] = useState(true)

  // Collect all annotated frame URLs from segments
  const frameUrls = aiLog.segments
    .flatMap(seg => seg.reasons?.map(r => r.annotatedFrameUrl).filter(Boolean) || []) as string[]

  const handleAnalyze = async () => {
    try {
      const result = await analyzeClip.mutateAsync({
        frameUrls,
        aiLog,
        clipFilename,
      })
      setGptResult(result.analysis)
      setShowGpt(true)
    } catch {
      setGptResult('Erro ao analisar com GPT. Verifique se o ChatGPT esta conectado.')
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 flex-wrap text-[10px] text-muted-foreground">
        <span>Frames: <span className="font-medium text-foreground">{aiLog.framesAnalyzed}</span></span>
        <span>Deteccoes: <span className={cn('font-medium', aiLog.clipHits > 0 ? 'text-yellow-600' : 'text-emerald-600')}>{aiLog.clipHits}</span></span>
        <span>Segmentos: <span className="font-medium text-foreground">{aiLog.segments.length}</span></span>
        {aiLog.modelUsed && <span>Modelo: <span className="font-medium text-blue-600">{aiLog.modelUsed}</span></span>}
      </div>

      {aiLog.categoryCounts && Object.keys(aiLog.categoryCounts).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(aiLog.categoryCounts).map(([cat, count]) => (
            <Badge key={cat} variant="outline" className={cn(
              'text-[8px] px-1.5 py-0 h-4',
              cat === 'sexual' && 'border-pink-500/50 text-pink-600',
              cat === 'violence' && 'border-red-500/50 text-red-600',
              cat === 'blood' && 'border-orange-500/50 text-orange-600',
            )}>
              {cat === 'sexual' ? 'Sexual' : cat === 'violence' ? 'Violencia' : cat === 'blood' ? 'Sangue' : cat} ({count as number})
            </Badge>
          ))}
        </div>
      )}

      {aiLog.segments.length > 0 ? (
        <div className="space-y-2">
          {aiLog.segments.map((seg, si) => (
            <div key={si} className={cn(
              'rounded-md border p-2 space-y-1',
              seg.category === 'sexual' && 'border-pink-500/30 bg-pink-500/5',
              seg.category === 'violence' && 'border-red-500/30 bg-red-500/5',
              seg.category === 'blood' && 'border-orange-500/30 bg-orange-500/5',
              !seg.category && 'border-yellow-500/30 bg-yellow-500/5',
            )}>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="font-mono font-bold">{seg.startSec}s — {seg.endSec}s</span>
                {seg.categoryLabel && (
                  <Badge variant="outline" className={cn(
                    'text-[8px] px-1.5 py-0 h-4',
                    seg.category === 'sexual' && 'border-pink-500/50 text-pink-600',
                    seg.category === 'violence' && 'border-red-500/50 text-red-600',
                    seg.category === 'blood' && 'border-orange-500/50 text-orange-600',
                  )}>
                    {seg.categoryLabel}
                  </Badge>
                )}
              </div>
              {seg.reasons && seg.reasons.length > 0 && (
                <div className="space-y-1.5 pl-2 border-l-2 border-muted-foreground/20">
                  {seg.reasons.map((r, ri) => (
                    <div key={ri} className="space-y-1">
                      <p className="text-[9px] text-muted-foreground">
                        <span className="font-mono text-foreground">{r.sec}s</span>
                        {' — "'}
                        <span className="italic">{r.label}</span>
                        {'" '}
                        <span className="text-[8px]">(score: {r.score})</span>
                      </p>
                      {r.annotatedFrameUrl && (
                        <img
                          src={r.annotatedFrameUrl}
                          alt={`Frame ${r.sec}s`}
                          className="rounded border border-border max-w-[280px] cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => window.open(r.annotatedFrameUrl, '_blank')}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-emerald-600">Nenhum conteudo restrito detectado</p>
      )}

      {/* GPT Review Button */}
      {frameUrls.length > 0 && openaiStatus?.connected && (
        <div className="pt-1 space-y-2">
          {!gptResult && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs h-7"
              onClick={handleAnalyze}
              disabled={analyzeClip.isPending}
            >
              {analyzeClip.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Bot className="h-3 w-3" />
              )}
              {analyzeClip.isPending ? 'Analisando...' : 'Revisar com GPT'}
            </Button>
          )}

          {gptResult && (
            <div className="rounded-md border border-violet-500/30 bg-violet-500/5 overflow-hidden">
              <button
                type="button"
                className="flex w-full items-center gap-2 p-2 text-left hover:bg-violet-500/10 transition-colors"
                onClick={() => setShowGpt(!showGpt)}
              >
                <Bot className="h-3.5 w-3.5 text-violet-600 shrink-0" />
                <span className="text-[10px] font-medium text-violet-700 dark:text-violet-400 flex-1">Analise GPT</span>
                <ChevronDown className={cn('h-3 w-3 text-violet-500 transition-transform', showGpt && 'rotate-180')} />
              </button>
              {showGpt && (
                <div className="px-3 pb-3 border-t border-violet-500/20">
                  <p className="text-[10px] text-foreground whitespace-pre-wrap pt-2 leading-relaxed">{gptResult}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="gap-1.5 text-[10px] h-6 mt-2 text-violet-600"
                    onClick={handleAnalyze}
                    disabled={analyzeClip.isPending}
                  >
                    {analyzeClip.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bot className="h-3 w-3" />}
                    Reanalisar
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
